import React, { useEffect, useMemo, useRef, useState } from "react";
import { CircleDot, Clock3, Layers3, ShieldCheck, X } from "lucide-react";
import ModalMotionShell from "./ModalMotionShell";
import ModalDragHandle from "./ModalDragHandle";

function useDraggable() {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const onMouseDown = (e) => {
    if (e.button !== 0) return;

    const startX = e.clientX - pos.x;
    const startY = e.clientY - pos.y;

    const onMove = (ev) => {
      setPos({
        x: ev.clientX - startX,
        y: ev.clientY - startY,
      });
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return { ref, pos, onMouseDown, setPos };
}

function useIsHandheldModal(open) {
  const [isHandheld, setIsHandheld] = useState(false);

  useEffect(() => {
    if (!open) return;

    const mediaQuery = window.matchMedia?.("(pointer: coarse)");

    const compute = () => {
      const isSmall = window.innerWidth < 768;
      setIsHandheld(Boolean(mediaQuery?.matches && isSmall));
    };

    compute();

    if (mediaQuery?.addEventListener) {
      mediaQuery.addEventListener("change", compute);
    } else if (mediaQuery?.addListener) {
      mediaQuery.addListener(compute);
    }

    window.addEventListener("resize", compute);

    return () => {
      if (mediaQuery?.removeEventListener) {
        mediaQuery.removeEventListener("change", compute);
      } else if (mediaQuery?.removeListener) {
        mediaQuery.removeListener(compute);
      }
      window.removeEventListener("resize", compute);
    };
  }, [open]);

  return isHandheld;
}

const SECTION_ORDER = ["focus", "completed", "dependency"];
const DEFAULT_ACTIVE_SECTION = "focus";

const SECTION_CONFIG = {
  focus: {
    Icon: CircleDot,
    accentClass: "bg-blue-600",
    iconWrapClass: "border-blue-100 bg-blue-50 text-blue-700",
    countClass: "border-blue-100 bg-blue-50 text-blue-700",
    activeTabClass: "border-blue-200 bg-blue-600 text-white shadow-blue-600/20",
    inactiveTabClass:
      "text-slate-600 hover:border-blue-100 hover:bg-blue-50/70 hover:text-blue-700",
    activeIconClass: "bg-white/15 text-white",
    inactiveIconClass: "bg-blue-50 text-blue-700",
  },
  completed: {
    Icon: ShieldCheck,
    accentClass: "bg-emerald-500",
    iconWrapClass: "border-emerald-100 bg-emerald-50 text-emerald-700",
    countClass: "border-emerald-100 bg-emerald-50 text-emerald-700",
    activeTabClass:
      "border-emerald-200 bg-emerald-600 text-white shadow-emerald-600/20",
    inactiveTabClass:
      "text-slate-600 hover:border-emerald-100 hover:bg-emerald-50/70 hover:text-emerald-700",
    activeIconClass: "bg-white/15 text-white",
    inactiveIconClass: "bg-emerald-50 text-emerald-700",
  },
  dependency: {
    Icon: Clock3,
    accentClass: "bg-amber-500",
    iconWrapClass: "border-amber-100 bg-amber-50 text-amber-700",
    countClass: "border-amber-100 bg-amber-50 text-amber-700",
    activeTabClass: "border-amber-200 bg-amber-500 text-white shadow-amber-500/20",
    inactiveTabClass:
      "text-slate-600 hover:border-amber-100 hover:bg-amber-50/70 hover:text-amber-700",
    activeIconClass: "bg-white/15 text-white",
    inactiveIconClass: "bg-amber-50 text-amber-700",
  },
};

function RoadmapTabs({ sections, activeSection, onSelect, c }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm">
      <div
        role="tablist"
        aria-label={c.tabAriaLabel}
        className={`grid grid-cols-1 gap-1.5 ${
          sections.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3"
        }`}
      >
        {sections.map((section) => {
          const config = SECTION_CONFIG[section.key];
          const Icon = config.Icon;
          const active = activeSection === section.key;

          return (
            <button
              key={section.key}
              type="button"
              role="tab"
              aria-selected={active}
              aria-controls={`roadmap-panel-${section.key}`}
              id={`roadmap-tab-${section.key}`}
              onClick={() => onSelect(section.key)}
              className={`group/tab flex min-h-[74px] items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left shadow-sm transition-all duration-300 ease-out active:scale-[0.99] ${
                active
                  ? `${config.activeTabClass} shadow-lg`
                  : `border-transparent bg-slate-50/80 ${config.inactiveTabClass}`
              }`}
            >
              <span className="flex min-w-0 items-center gap-3">
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover/tab:scale-110 group-hover/tab:shadow-md motion-reduce:transition-none motion-reduce:group-hover/tab:scale-100 ${
                    active ? config.activeIconClass : config.inactiveIconClass
                  }`}
                >
                  <Icon className="h-5 w-5 transition-transform duration-300 ease-out group-hover/tab:scale-105 motion-reduce:group-hover/tab:scale-100" />
                </span>

                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold md:text-[15px]">
                    {section.tabTitle || section.title}
                  </span>
                  <span
                    className={`mt-0.5 block truncate text-xs ${
                      active ? "text-white/75" : "text-slate-500"
                    }`}
                  >
                    {section.summaryLabel}
                  </span>
                </span>
              </span>

              <span
                className={`inline-flex h-7 min-w-7 shrink-0 items-center justify-center rounded-full px-2 text-xs font-semibold ${
                  active
                    ? "bg-white/15 text-white"
                    : "bg-white text-slate-500 ring-1 ring-slate-200"
                }`}
              >
                {section.items.length}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RoadmapActiveSection({ section, c }) {
  if (!section) return null;

  const config = SECTION_CONFIG[section.key];
  const Icon = config.Icon;

  return (
    <section
      id={`roadmap-panel-${section.key}`}
      role="tabpanel"
      aria-labelledby={`roadmap-tab-${section.key}`}
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
    >
      <div className={`h-[3px] ${config.accentClass}`} />

      <div className="border-b border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-50/70 px-5 py-5 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <div
              className={`mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border shadow-sm ${config.iconWrapClass}`}
            >
              <Icon className="h-5 w-5" />
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-base font-semibold text-slate-950">
                  {section.title}
                </h3>

                <span
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold shadow-sm ${config.countClass}`}
                >
                  <Layers3 className="h-3.5 w-3.5" />
                  {section.items.length} {c.itemsLabel}
                </span>
              </div>

              <p className="mt-1.5 max-w-4xl text-sm leading-6 text-slate-500">
                {section.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="divide-y divide-slate-200">
        {section.items.map((item, idx) => (
          <RoadmapRow
            key={`${section.key}-${item.title}-${idx}`}
            item={item}
            sectionKey={section.key}
            c={c}
          />
        ))}
      </div>
    </section>
  );
}

function RoadmapRow({ item, sectionKey, c }) {
  const iconScale = item.iconScale ?? 1;
  const iconPadding = item.iconPadding ?? "p-1.5";
  const iconHoverScale = +(iconScale * 1.08).toFixed(3);

  return (
    <div className="group/row px-5 py-5 transition-colors duration-300 hover:bg-slate-50/70 sm:px-6">
      <div className="flex items-start gap-5">
        <div
          className={`flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover/row:scale-[1.04] group-hover/row:-translate-y-0.5 group-hover/row:border-slate-300 group-hover/row:bg-white group-hover/row:shadow-lg group-hover/row:shadow-slate-900/10 motion-reduce:transition-none motion-reduce:group-hover/row:scale-100 motion-reduce:group-hover/row:translate-y-0 ${iconPadding}`}
        >
          <img
            src={item.icon}
            alt={item.iconAlt}
            className="h-full w-full object-contain transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform group-hover/row:[transform:scale(var(--roadmap-icon-hover-scale))] motion-reduce:group-hover/row:[transform:scale(var(--roadmap-icon-scale))]"
            style={{
              "--roadmap-icon-scale": iconScale,
              "--roadmap-icon-hover-scale": iconHoverScale,
              transform: "scale(var(--roadmap-icon-scale))",
            }}
          />
        </div>

        <div className="min-w-0 flex-1">
          <h4 className="text-[15px] font-semibold text-slate-900 md:text-base">
            {item.title}
          </h4>

          {item.goalSecondary ? (
            <div className="mt-1.5 space-y-1.5 text-sm leading-6 text-slate-600">
              <p>{item.goal}</p>
              <p>{item.goalSecondary}</p>
            </div>
          ) : (
            <p className="mt-1.5 text-sm leading-6 text-slate-600">{item.goal}</p>
          )}

          {item.dependency && (
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 transition-colors duration-300 group-hover/row:border-slate-300 group-hover/row:bg-white">
              <p className="text-xs leading-5 text-slate-600">
                <span className="font-semibold text-slate-700">
                  {c.dependencyLabel}:
                </span>{" "}
                {item.dependency}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function RoadmapModal({ open, onClose, language }) {
  const {
    ref: dragRef,
    pos: dragPos,
    onMouseDown: dragOnMouseDown,
    setPos: setDragPos,
  } = useDraggable();

  const isHandheld = useIsHandheldModal(open);
  const [activeSection, setActiveSection] = useState(DEFAULT_ACTIVE_SECTION);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (open) {
      setDragPos({ x: 0, y: 0 });
      setActiveSection(DEFAULT_ACTIVE_SECTION);
    }
  }, [open, setDragPos]);

  const t = {
    de: {
      title: "Roadmap",
      subtitle:
        "Klar gegliederte Übersicht über aktuelle Fokusbereiche, abgeschlossene Meilensteine und externe Abhängigkeiten",
      tabAriaLabel: "Roadmap-Kategorien",
      itemsLabel: "Einträge",
      dependencyLabel: "Abhängigkeit",
      sections: {
        focus: {
          title: "Aktueller Fokus",
          tabTitle: "Aktueller Fokus",
          summaryLabel: "Aktiv in Arbeit",
          description:
            "Priorisierte Erweiterungen, die aktuell aktiv vorangetrieben werden.",
        },
        completed: {
          title: "Abgeschlossen",
          tabTitle: "Abgeschlossen",
          summaryLabel: "Bereits live",
          description: "Bereits umgesetzte Meilensteine, die in V3Tax verfügbar sind.",
        },
        dependency: {
          title: "On Hold / Externe Abhängigkeiten",
          tabTitle: "On Hold",
          summaryLabel: "Externe Schritte",
          description:
            "Geplante Integrationen, die auf Partner, Exporte oder technische Kooperationen angewiesen sind.",
        },
      },
      items: [
        {
          title: "Groncard Integration",
          goal:
            "Unterstützung von Transaktionen im Zusammenhang mit der Nutzung von Groncard-Gutscheinen.",
          statusKey: "focus",
          dependency:
            "Die Umsetzung ist von einer technischen Zusammenarbeit mit Groncard abhängig.",
          icon: "/Groncard.jpg",
          iconAlt: "Groncard",
          iconScale: 1.28,
        },
        {
          title: "StellaPay Integration",
          goal: "Import und steuerliche Verarbeitung von StellaPay-Transaktionen.",
          statusKey: "focus",
          dependency:
            "Eine Integration setzt die Bereitstellung eines Transaktions-Exports, zum Beispiel CSV, durch StellaPay voraus.",
          icon: "/Stellapay.png",
          iconAlt: "StellaPay",
        },
        {
          title: "Greencart+ | Debit Card",
          goal:
            "Die Erkennung und steuerliche Behandlung der relevanten Greencart+ Debit-Card-Flows ist abgeschlossen, einschließlich Cashback-Rewards, kartenbezogener On-Chain-Aktivität und jurisdiktionsspezifischer steuerlicher Behandlung.",
          statusKey: "completed",
          icon: "/Greencart.png",
          iconAlt: "Greencart+ | Debit Card",
        },
        {
          title: "Mugshot Pro",
          goal:
            "Die präzise Erkennung und steuerliche Behandlung von Mugshot-Pro-Upgrade-Transaktionen ist abgeschlossen, einschließlich der zugehörigen On-Chain-Flows und Veräußerungsbehandlung, sofern anwendbar.",
          statusKey: "completed",
          icon: "/Mugshot.png",
          iconAlt: "Mugshot Pro",
          iconPadding: "p-1",
          iconScale: 1.68,
        },
        {
          title: "UK Tax Rules",
          goal:
            "Die UK-Steuerunterstützung mit GBP-Bewertung, Section-104-Pooling, Same-Day-Matching und 30-Day-Matching ist abgeschlossen.",
          statusKey: "completed",
          icon: "/UK.svg.png",
          iconAlt: "UK",
        },
        {
          title: "VeWorld Mobile Dashboard & Smart Scan",
          goal:
            "V3Tax ist jetzt mobil in VeWorld verfügbar: Smart Scan liest nach dem Verbinden sofort potenziell steuerrelevante On-Chain-Aktivität der verbundenen Wallet aus.",
          goalSecondary:
            "Gekaufte Wallet-Sets erhalten ein mobiles Tax Dashboard, mit dem das laufende Steuerjahr unterjährig verfolgt und neu berechnet werden kann.",
          statusKey: "completed",
          icon: "/veworld.png",
          iconAlt: "VeWorld",
        },
        {
          title: "Accuracy Update",
          goal:
            "Stündliche Marktdaten von etablierten Preisquellen, historische On-Chain-DEX-Preise wo anwendbar, intelligente Interpolation zwischen verifizierten Preispunkten und strengere Qualitätsprüfungen. Transaktionen ohne belastbaren Preis werden von der Steuerberechnung ausgeschlossen und im Frontend klar markiert.",
          statusKey: "completed",
          icon: "/v13-accuracy.svg",
          iconAlt: "Accuracy Update",
        },
        {
          title: "USA Integration",
          goal: "Die US-Steuerlogik inklusive Berechnung in US-Dollar ist abgeschlossen.",
          statusKey: "completed",
          icon: "/US.svg.png",
          iconAlt: "USA",
        },
      ],
      close: "Schließen",
    },
    en: {
      title: "Roadmap",
      subtitle:
        "Clearly structured overview of current focus areas, completed milestones and external dependencies",
      tabAriaLabel: "Roadmap categories",
      itemsLabel: "items",
      dependencyLabel: "Dependency",
      sections: {
        focus: {
          title: "Current Focus",
          tabTitle: "Current Focus",
          summaryLabel: "Active work",
          description: "Prioritized extensions that are currently being actively worked on.",
        },
        completed: {
          title: "Completed",
          tabTitle: "Completed",
          summaryLabel: "Already live",
          description: "Completed milestones that are already available in V3Tax.",
        },
        dependency: {
          title: "On Hold / External Dependencies",
          tabTitle: "On Hold",
          summaryLabel: "External steps",
          description:
            "Planned integrations that depend on partners, export formats or technical cooperation.",
        },
      },
      items: [
        {
          title: "Groncard Integration",
          goal: "Support for transactions related to the use of Groncard voucher transactions.",
          statusKey: "focus",
          dependency: "Implementation depends on technical cooperation with Groncard.",
          icon: "/Groncard.jpg",
          iconAlt: "Groncard",
          iconScale: 1.28,
        },
        {
          title: "StellaPay Integration",
          goal: "Import and tax processing of StellaPay transactions.",
          statusKey: "focus",
          dependency:
            "Integration requires the availability of transaction exports, for example CSV, provided by StellaPay.",
          icon: "/Stellapay.png",
          iconAlt: "StellaPay",
        },
        {
          title: "Greencart+ | Debit Card",
          goal:
            "Recognition and tax treatment of the relevant Greencart+ Debit Card flows has been completed, including cashback rewards, card-related on-chain activity and jurisdiction-specific tax treatment.",
          statusKey: "completed",
          icon: "/Greencart.png",
          iconAlt: "Greencart+ | Debit Card",
        },
        {
          title: "Mugshot Pro",
          goal:
            "Precise recognition and tax treatment of Mugshot Pro upgrade transactions has been completed, including the corresponding on-chain flows and disposal handling where applicable.",
          statusKey: "completed",
          icon: "/Mugshot.png",
          iconAlt: "Mugshot Pro",
          iconPadding: "p-1",
          iconScale: 1.68,
        },
        {
          title: "UK Tax Rules",
          goal:
            "UK tax support with GBP valuation, Section 104 pooling, Same-Day matching and 30-Day matching has been completed.",
          statusKey: "completed",
          icon: "/UK.svg.png",
          iconAlt: "UK",
        },
        {
          title: "VeWorld Mobile Dashboard & Smart Scan",
          goal:
            "V3Tax is now available on mobile inside VeWorld: Smart Scan immediately reads potentially tax-relevant on-chain activity from the connected wallet.",
          goalSecondary:
            "Purchased Wallet Sets get a mobile Tax Dashboard, allowing the current tax year to be tracked and recalculated throughout the year.",
          statusKey: "completed",
          icon: "/veworld.png",
          iconAlt: "VeWorld",
        },
        {
          title: "Accuracy Update",
          goal:
            "Hourly market data from established price sources, historical on-chain DEX pricing where applicable, smart interpolation between verified price points, and stricter quality checks. Transactions without a reliable price are excluded from tax calculation and clearly marked in the frontend.",
          statusKey: "completed",
          icon: "/v13-accuracy.svg",
          iconAlt: "Accuracy Update",
        },
        {
          title: "USA Integration",
          goal: "US tax logic including calculations in US dollars has been completed.",
          statusKey: "completed",
          icon: "/US.svg.png",
          iconAlt: "USA",
        },
      ],
      close: "Close",
    },
  };

  const c = t[language] || t.de;

  const roadmapSections = useMemo(
    () =>
      SECTION_ORDER.map((key) => ({
        key,
        title: c.sections[key].title,
        tabTitle: c.sections[key].tabTitle,
        summaryLabel: c.sections[key].summaryLabel,
        description: c.sections[key].description,
        items: c.items.filter((item) => item.statusKey === key),
      })).filter((section) => section.items.length > 0),
    [c]
  );

  const activeRoadmapSection =
    roadmapSections.find((section) => section.key === activeSection) || roadmapSections[0];

  return (
    <ModalMotionShell
      isOpen={open}
      onClose={onClose}
      stretch={isHandheld}
      backdropClassName={!isHandheld ? "!px-4" : ""}
    >
      <div
        ref={dragRef}
        style={
          isHandheld
            ? {
                position: "fixed",
                inset: 0,
                height: "100dvh",
                maxHeight: "100dvh",
                zIndex: 10001,
                paddingTop: "max(env(safe-area-inset-top), 12px)",
                paddingBottom: "max(env(safe-area-inset-bottom), 12px)",
              }
            : {
                transform: `translate(${dragPos.x}px, ${dragPos.y}px)`,
                maxHeight: "calc(100dvh - 3rem)",
              }
        }
        className={`overflow-hidden bg-white ${
          isHandheld
            ? "flex h-full min-h-0 w-full flex-col rounded-none shadow-none"
            : "flex h-max max-h-[calc(100dvh-3rem)] w-[min(calc(100vw-3rem),86rem)] max-w-none flex-col rounded-2xl shadow-2xl"
        }`}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="h-[5px] shrink-0 bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600" />

        <div
          className={`shrink-0 border-b border-slate-200 bg-white ${
            isHandheld ? "px-4 py-3" : "cursor-move select-none px-8 py-5"
          } flex items-start justify-between gap-4`}
          onMouseDown={isHandheld ? undefined : dragOnMouseDown}
        >
          <div className="flex min-w-0 items-center gap-3">
            {!isHandheld && (
              <ModalDragHandle
                onMouseDown={dragOnMouseDown}
                title={language === "en" ? "Move" : "Verschieben"}
                ariaLabel={language === "en" ? "Move modal" : "Modal verschieben"}
                className="mr-2 mt-0.5"
              />
            )}

            <div className="min-w-0">
              <h2
                className={`truncate font-semibold text-slate-900 ${
                  isHandheld ? "text-lg" : "text-xl"
                }`}
              >
                {c.title}
              </h2>
              <p className="mt-0.5 text-sm text-slate-500">{c.subtitle}</p>
            </div>
          </div>

          <button
            onClick={onClose}
            onMouseDown={(e) => e.stopPropagation()}
            className="group -m-2 flex-shrink-0 p-2 text-gray-400 transition hover:text-gray-600"
            aria-label={language === "en" ? "Close" : "Schließen"}
          >
            <X className="h-5 w-5 transition-transform duration-200 group-hover:rotate-90" />
          </button>
        </div>

        <div
          className={`overflow-y-auto overscroll-contain ${
            isHandheld
              ? "min-h-0 flex-1 px-4 py-4 pb-6"
              : "max-h-[calc(100dvh-14rem)] px-8 py-8"
          }`}
        >
          <RoadmapTabs
            sections={roadmapSections}
            activeSection={activeRoadmapSection.key}
            onSelect={setActiveSection}
            c={c}
          />

          <div className="mt-6">
            <RoadmapActiveSection section={activeRoadmapSection} c={c} />
          </div>
        </div>

        <div
          className={`shrink-0 border-t border-slate-200 bg-white ${
            isHandheld ? "px-4 py-3" : "flex justify-end px-8 py-5"
          }`}
        >
          <div className={isHandheld ? "flex justify-end" : ""}>
            <button
              onClick={onClose}
              className="rounded-lg bg-blue-600 px-6 py-2 font-medium text-white shadow-sm transition-all duration-200 hover:bg-blue-700 hover:shadow-md active:scale-95"
            >
              {c.close}
            </button>
          </div>
        </div>
      </div>
    </ModalMotionShell>
  );
}
